import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../utils/api';

// API Base URL (이미지 경로용)
const API_BASE_URL = 'https://www.hb-volleyball.co.kr/api';

const EventsContainer = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 2rem;
`;

const EventsList = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const EventCard = styled.div<{ status?: string; isApplied?: boolean }>`
  padding: 1.5rem;
  border: 2px solid ${props => {
    if (props.isApplied) return '#000000';
    if (props.status === '진행중') return '#d1e7dd';
    if (props.status === '마감') return '#fff3cd';
    return '#dee2e6';
  }};
  border-radius: 12px;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  cursor: pointer;
  background: ${props => {
    if (props.isApplied) return '#f8f9fa';
    if (props.status === '진행중') return '#f8fff9';
    return 'white';
  }};
  
  &:hover {
    border-color: #000000;
    background: #f0f0f0;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const EventHeader = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0.75rem;
  gap: 0.75rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }
`;

const EventTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: #000000;
  width: 100%;
  order: 1;
  
  @media (min-width: 768px) {
    flex: 1;
    order: 0;
  }
`;

const EventMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  width: 100%;
  order: 2;
  
  @media (min-width: 768px) {
    width: auto;
    order: 0;
  }
`;

const BadgeContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
`;

const StatusBadge = styled.span<{ status?: string }>`
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => {
    if (props.status === '진행중') return '#d1e7dd';
    if (props.status === '마감') return '#fff3cd';
    return '#dee2e6';
  }};
  color: ${props => {
    if (props.status === '진행중') return '#0f5132';
    if (props.status === '마감') return '#856404';
    return '#6c757d';
  }};
  white-space: nowrap;
`;

const AppliedBadge = styled.span`
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  background: #000000;
  color: white;
  white-space: nowrap;
`;

const EventInfo = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  line-height: 1.6;
  margin-bottom: 0.75rem;
`;

const EventMeta = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.8125rem;
  color: #6c757d;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #dee2e6;
  flex-wrap: wrap;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #adb5bd;
  font-size: 1rem;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    border-radius: 24px 24px 0 0;
    max-height: 80vh;
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  
  &:hover {
    color: #000000;
  }
`;

const ViewSection = styled.div`
  margin-bottom: 1.5rem;
`;

const DetailRow = styled.div`
  padding: 0.75rem 0;
  border-bottom: 1px solid #dee2e6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.div`
  font-size: 0.8125rem;
  color: #6c757d;
  font-weight: 600;
  margin-bottom: 0.375rem;
`;

const DetailValue = styled.div`
  font-size: 0.9375rem;
  color: #000000;
  font-weight: 500;
`;

const DetailContent = styled.div`
  font-size: 0.9375rem;
  color: #495057;
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-word;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-top: 1rem;
`;

const ApplicationForm = styled.div`
  margin: 1.5rem 0;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
`;

const FormTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1rem;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ImageModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 10000;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ImageModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: white;
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ImageModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 1rem;
`;

const ImageModalTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const ImageModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  
  &:hover {
    color: #000000;
  }
`;

const ImageContainer = styled.div`
  max-width: 100%;
  max-height: calc(90vh - 80px);
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: calc(90vh - 80px);
  object-fit: contain;
  border-radius: 8px;
`;

const ImageButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const ImageButton = styled.button`
  padding: 0.5rem 1rem;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e9ecef;
    border-color: #adb5bd;
  }
  
  &:active {
    background: #dee2e6;
  }
`;

interface Event {
  id: number;
  title: string;
  description: string;
  eventType: string;
  startDate: string;
  endDate: string;
  openDatetime?: string | null;
  maxParticipants: string;
  currentParticipants: number;
  status: '진행중' | '마감' | '종료';
  isApplied?: boolean;
}

const CountdownDisplay: React.FC<{ openDatetime: string }> = ({ openDatetime }) => {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    isOpen: boolean;
  } | null>(null);

  useEffect(() => {
    const calculateTimeLeft = () => {
      try {
        if (!openDatetime) {
          console.warn('openDatetime이 없습니다');
          setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, isOpen: true });
          return;
        }
        
        const now = new Date().getTime();
        // 'YYYY-MM-DD HH:mm:ss' 형식을 ISO 형식으로 변환
        const isoFormat = openDatetime.replace(' ', 'T');
        console.log('ISO 형식 변환:', isoFormat);
        const openTime = new Date(isoFormat).getTime();
        console.log('현재 시간:', now, '오픈 시간:', openTime);
        
        // 유효하지 않은 날짜인지 확인
        if (isNaN(openTime)) {
          console.error('유효하지 않은 날짜 형식:', openDatetime);
          setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, isOpen: true });
          return;
        }
        
        const difference = openTime - now;
        console.log('시간 차이 (ms):', difference);

        if (difference > 0) {
          const days = Math.floor(difference / (1000 * 60 * 60 * 24));
          const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((difference % (1000 * 60)) / 1000);
          console.log('계산된 시간:', { days, hours, minutes, seconds });
          setTimeLeft({ days, hours, minutes, seconds, isOpen: false });
        } else {
          console.log('오픈 시간이 지났습니다');
          setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, isOpen: true });
        }
      } catch (error) {
        console.error('시간 계산 중 오류:', error);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, isOpen: true });
      }
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [openDatetime]);

  // 초기 로딩 중이면 로딩 표시
  if (timeLeft === null) {
    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 0.75rem',
        background: '#fff3cd',
        borderRadius: '8px',
        fontSize: '0.875rem',
        fontWeight: 600,
        color: '#856404'
      }}>
        <span>계산 중...</span>
      </div>
    );
  }

  const { days, hours, minutes, seconds, isOpen } = timeLeft;

  // 오픈 시간이 지났으면 표시하지 않음
  if (isOpen) return null;

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 0.75rem',
      background: '#fff3cd',
      borderRadius: '8px',
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#856404'
    }}>
      <span>D-{String(days).padStart(2, '0')}</span>
      <span style={{ color: '#6c757d' }}>:</span>
      <span>{String(hours).padStart(2, '0')}</span>
      <span style={{ color: '#6c757d' }}>:</span>
      <span>{String(minutes).padStart(2, '0')}</span>
      <span style={{ color: '#6c757d' }}>:</span>
      <span>{String(seconds).padStart(2, '0')}</span>
    </div>
  );
};

const MemberEvents: React.FC = () => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [applicationData, setApplicationData] = useState('');
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasUniformNumber, setHasUniformNumber] = useState(false);
  const [desiredNumber, setDesiredNumber] = useState('');
  const [numberError, setNumberError] = useState('');
  const [nickname, setNickname] = useState('');
  const [nicknameError, setNicknameError] = useState('');
  const [upperSize, setUpperSize] = useState('');
  const [lowerSize, setLowerSize] = useState('');
  const [sizeError, setSizeError] = useState('');
  const [isBeforeOpenTime, setIsBeforeOpenTime] = useState(false);
  const [availableNumbers, setAvailableNumbers] = useState<string[]>([]);
  const [loadingAvailableNumbers, setLoadingAvailableNumbers] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState<string>('');
  const [currentImageTitle, setCurrentImageTitle] = useState<string>('');

  // 내 유니폼 정보 조회 (v2 배번 보유 여부만 확인)
  const fetchMyUniform = async () => {
    try {
      const response = await apiClient.get('/uniforms/my-uniform');
      if (response.data.success) {
        const uniforms = response.data.data || [];
        const hasNumber = uniforms.some((u: any) => u.version === 2 && u.number);
        setHasUniformNumber(hasNumber);
      }
    } catch (error) {
      console.error('내 유니폼 정보 조회 실패:', error);
    }
  };

  // 이벤트 목록 조회 (진행중인 이벤트만)
  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/events');
      console.log('이벤트 API 응답:', response.data);
      if (response.data.success) {
        // 진행중인 이벤트만 필터링
        let ongoingEvents = response.data.data.filter((e: Event) => e.status === '진행중');
        console.log('진행중인 이벤트:', ongoingEvents);
        console.log('이벤트 openDatetime 확인:', ongoingEvents.map((e: Event) => ({ id: e.id, title: e.title, openDatetime: e.openDatetime })));
        
        // 배번변경 이벤트는 배번이 있는 회원에게만 표시
        ongoingEvents = ongoingEvents.filter((e: Event) => {
          if (e.eventType === '배번변경') {
            return hasUniformNumber;
          }
          return true;
        });
        
        setEvents(ongoingEvents);
      }
    } catch (error) {
      console.error('이벤트 목록 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchMyUniform();
    }
  }, [user]);

  // hasUniformNumber가 설정된 후에 이벤트 목록 조회
  useEffect(() => {
    if (user) {
      fetchEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hasUniformNumber]);

  const handleEventClick = async (event: Event) => {
    setSelectedEvent(event);
    setApplicationData('');
    setDesiredNumber('');
    setNumberError('');
    setNickname('');
    setNicknameError('');
    setUpperSize('');
    setLowerSize('');
    setSizeError('');
    setAvailableNumbers([]);
    setIsModalOpen(true);
    
    // 오픈 시간 체크 초기화 (백엔드와 동일한 로직: KST 기준)
    if (event.openDatetime) {
      try {
        const [datePart, timePart] = event.openDatetime.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes, seconds] = timePart.split(':').map(Number);
        
        // KST 시간으로 Date 객체 생성
        const openTimeKST = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds || 0) - 9 * 3600 * 1000);
        const now = new Date();
        
        setIsBeforeOpenTime(now < openTimeKST);
      } catch (error) {
        console.error('오픈 시간 체크 중 오류:', error);
        setIsBeforeOpenTime(false);
      }
    } else {
      setIsBeforeOpenTime(false);
    }
    
    // 배번할당 이벤트인 경우 사용 가능한 배번 목록 조회
    if (event.eventType === '배번할당') {
      try {
        setLoadingAvailableNumbers(true);
        const response = await apiClient.get(`/events/${event.id}/available-numbers`);
        if (response.data.success) {
          setAvailableNumbers(response.data.data.available_numbers || []);
        }
      } catch (error) {
        console.error('사용 가능한 배번 목록 조회 실패:', error);
        setAvailableNumbers([]);
      } finally {
        setLoadingAvailableNumbers(false);
      }
      
      // 이미 참여한 경우 기존 신청 정보 불러오기
      if (event.isApplied) {
        try {
          // 이벤트 참여자 목록에서 내 정보 찾기
          const participantsResponse = await apiClient.get(`/events/${event.id}/participants`);
          if (participantsResponse.data.success && user) {
            const myParticipant = participantsResponse.data.data.find((p: any) => p.user_no === user.user_no);
            if (myParticipant) {
              // 기존 정보 채우기
              if (myParticipant.desired_number) {
                setDesiredNumber(myParticipant.desired_number);
              }
              
              // application_data에서 닉네임 추출
              if (myParticipant.application_data) {
                try {
                  const appData = typeof myParticipant.application_data === 'string' 
                    ? JSON.parse(myParticipant.application_data) 
                    : myParticipant.application_data;
                  if (appData.nickname) {
                    setNickname(appData.nickname);
                  }
                } catch (e) {
                  console.error('application_data 파싱 실패:', e);
                }
              }
              
              // uniform_size에서 상의/하의 사이즈 추출
              if (myParticipant.uniform_size) {
                const sizeMatch = myParticipant.uniform_size.match(/상의\s+([^,]+),\s*하의\s+(.+)/);
                if (sizeMatch) {
                  setUpperSize(sizeMatch[1].trim());
                  setLowerSize(sizeMatch[2].trim());
                }
              }
            }
          }
        } catch (error) {
          console.error('기존 신청 정보 조회 실패:', error);
        }
      }
    }
  };
  
  // 모달이 열려있을 때 오픈 시간 실시간 체크
  useEffect(() => {
    if (!isModalOpen || !selectedEvent?.openDatetime) {
      return;
    }
    
    const checkOpenTime = () => {
      try {
        // 백엔드와 동일: KST 기준으로 비교
        const [datePart, timePart] = selectedEvent.openDatetime!.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes, seconds] = timePart.split(':').map(Number);
        
        // KST 시간으로 Date 객체 생성
        const openTimeKST = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds || 0) - 9 * 3600 * 1000);
        const now = new Date();
        
        setIsBeforeOpenTime(now < openTimeKST);
      } catch (error) {
        console.error('오픈 시간 체크 중 오류:', error);
        setIsBeforeOpenTime(false);
      }
    };
    
    // 즉시 체크
    checkOpenTime();
    
    // 1초마다 체크
    const interval = setInterval(checkOpenTime, 1000);
    
    return () => clearInterval(interval);
  }, [isModalOpen, selectedEvent?.openDatetime]);

  const handleApply = async () => {
    if (!selectedEvent) return;

    // 오픈 시간 체크 (백엔드와 동일한 로직: KST 기준)
    // openDatetime은 KST로 저장되어 있으므로, KST 기준으로 비교
    if (selectedEvent.openDatetime) {
      try {
        // 'YYYY-MM-DD HH:mm:ss' 형식을 KST로 해석
        const [datePart, timePart] = selectedEvent.openDatetime.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes, seconds] = timePart.split(':').map(Number);
        
        // KST 시간으로 Date 객체 생성
        const openTimeKST = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds || 0) - 9 * 3600 * 1000);
        
        // 현재 시간을 KST로 변환
        const now = new Date();
        const nowKST = new Date(now.getTime() + (9 * 3600 * 1000) - (now.getTimezoneOffset() * 60 * 1000));
        
        console.log('신청 시 오픈 시간 체크 (KST 기준):', {
          openDatetime: selectedEvent.openDatetime,
          now: now.toISOString(),
          nowKST: new Date(nowKST.getTime() - 9 * 3600 * 1000).toISOString(),
          openTimeKST: openTimeKST.toISOString(),
          difference: openTimeKST.getTime() - now.getTime(),
          isBefore: now < openTimeKST
        });
        
        if (now < openTimeKST) {
          const diffSeconds = Math.floor((openTimeKST.getTime() - now.getTime()) / 1000);
          const diffMinutes = Math.floor(diffSeconds / 60);
          const diffHours = Math.floor(diffMinutes / 60);
          alert(`이벤트 오픈 시간 전입니다.\n오픈 시간: ${selectedEvent.openDatetime}\n남은 시간: ${diffHours}시간 ${diffMinutes % 60}분 ${diffSeconds % 60}초`);
          return;
        }
      } catch (error) {
        console.error('오픈 시간 체크 중 오류:', error, selectedEvent.openDatetime);
      }
    }

    // 배번할당 이벤트는 desired_number, nickname, uniform_size 필수
    if (selectedEvent.eventType === '배번할당') {
      if (!desiredNumber || !desiredNumber.trim()) {
        setNumberError('희망 배번을 입력해주세요');
        return;
      }
      
      // 숫자만 입력 가능 (1-99)
      const num = parseInt(desiredNumber.trim());
      if (isNaN(num) || num < 1 || num > 99) {
        setNumberError('1-99 사이의 숫자를 입력해주세요');
        return;
      }
      
      if (!nickname || !nickname.trim()) {
        setNicknameError('닉네임을 입력해주세요');
        return;
      }
      
      if (!upperSize || !upperSize.trim()) {
        setSizeError('상의 사이즈를 입력해주세요');
        return;
      }
      
      if (!lowerSize || !lowerSize.trim()) {
        setSizeError('하의 사이즈를 입력해주세요');
        return;
      }
    }

    try {
      const requestData: any = {};
      
      // 배번할당 이벤트는 desired_number, uniform_size, nickname 전송
      if (selectedEvent.eventType === '배번할당') {
        // 이미 참여한 경우 배번은 전송하지 않음 (수정 불가)
        if (!selectedEvent.isApplied) {
          requestData.desired_number = desiredNumber.trim();
        }
        // 상의와 하의 사이즈를 합쳐서 전송 (예: "상의 S, 하의 S" 또는 "상의 w M, 하의 w M")
        const sizeText = `상의 ${upperSize.trim()}, 하의 ${lowerSize.trim()}`;
        requestData.uniform_size = sizeText;
        // 닉네임은 application_data에 JSON으로 포함
        requestData.application_data = JSON.stringify({
          nickname: nickname.trim()
        });
      }
      
      // 이미 참여한 경우 수정, 아닌 경우 신청
      let response;
      if (selectedEvent.isApplied) {
        response = await apiClient.put(`/events/${selectedEvent.id}/apply`, requestData);
      } else {
        response = await apiClient.post(`/events/${selectedEvent.id}/apply`, requestData);
      }
      
      if (response.data.success) {
        alert(selectedEvent.isApplied ? '이벤트 참가 정보가 수정되었습니다!' : '이벤트 참가 신청이 완료되었습니다!');
        fetchEvents(); // 목록 새로고침
        setIsModalOpen(false);
        setDesiredNumber('');
        setNumberError('');
        setNickname('');
        setNicknameError('');
        setUpperSize('');
        setLowerSize('');
        setSizeError('');
      }
    } catch (error: any) {
      console.error('이벤트 참가 신청 실패:', error);
      const errorMessage = error.response?.data?.detail || '이벤트 참가 신청에 실패했습니다';
      
      // 배번할당 이벤트의 경우 에러 메시지에 따라 적절한 필드에 표시
      if (selectedEvent.eventType === '배번할당') {
        if (errorMessage.includes('번호') || errorMessage.includes('배번')) {
          setNumberError(errorMessage);
        } else if (errorMessage.includes('닉네임')) {
          setNicknameError(errorMessage);
        } else if (errorMessage.includes('사이즈') || errorMessage.includes('size')) {
          setSizeError(errorMessage);
        } else {
          alert(errorMessage);
        }
      } else {
        alert(errorMessage);
      }
    }
  };

  const handleCancelApply = async () => {
    if (!selectedEvent) return;

    if (window.confirm('이벤트 참가를 취소하시겠습니까?')) {
      try {
        const response = await apiClient.delete(`/events/${selectedEvent.id}/apply`);
        
        if (response.data.success) {
          alert('이벤트 참가가 취소되었습니다.');
          fetchEvents(); // 목록 새로고침
          setIsModalOpen(false);
        }
      } catch (error: any) {
        console.error('이벤트 참가 취소 실패:', error);
        alert(error.response?.data?.detail || '이벤트 참가 취소에 실패했습니다');
      }
    }
  };

  return (
    <DashboardLayout title="이벤트">
      <EventsContainer>
        <Title>이벤트</Title>
        
        <EventsList>
          {loading ? (
            <EmptyState>로딩 중...</EmptyState>
          ) : events.length > 0 ? (
            events.map(event => (
              <EventCard 
                key={event.id} 
                status={event.status}
                isApplied={event.isApplied}
                onClick={() => handleEventClick(event)}
              >
                <EventHeader>
                  <EventTitle>🎉 {event.title}</EventTitle>
                  <EventMetaRow>
                    <BadgeContainer>
                      {event.isApplied && <AppliedBadge>참가중</AppliedBadge>}
                      <StatusBadge status={event.status}>{event.status}</StatusBadge>
                    </BadgeContainer>
                  </EventMetaRow>
                </EventHeader>
                {(() => {
                      // 여기입니다~ 실시간으로 1초씩 줄어들어서 나오는 디데이 표시기
                      return event.openDatetime ? (
                        <CountdownDisplay openDatetime={event.openDatetime} />
                      ) : null;
                    })()}
                {event.openDatetime && (
                  <div style={{ 
                    marginBottom: '0.75rem',
                    fontSize: '0.8125rem', 
                    color: '#6c757d',
                    fontWeight: 600
                  }}>
                    ⏰ 오픈 시간: {event.openDatetime}
                  </div>
                )}
                <EventInfo>{event.description}</EventInfo>
                <EventMeta>
                  <span>📅 {event.startDate} ~ {event.endDate}</span>
                  <span>•</span>
                  <span>👥 {event.currentParticipants}/{event.maxParticipants}명</span>
                  <span>•</span>
                  <span>🏷️ {event.eventType}</span>
                </EventMeta>
              </EventCard>
            ))
          ) : (
            <EmptyState>진행 중인 이벤트가 없습니다.</EmptyState>
          )}
        </EventsList>
        
        {/* 이벤트 상세 및 참가 신청 모달 */}
        <ModalOverlay 
          isOpen={isModalOpen}
          onClick={() => setIsModalOpen(false)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>이벤트 상세</ModalTitle>
              <CloseButton onClick={() => setIsModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            
            {selectedEvent && (
              <>
                <ViewSection>
                  <DetailRow>
                    <DetailLabel>제목</DetailLabel>
                    <DetailValue>{selectedEvent.title}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>이벤트 타입</DetailLabel>
                    <DetailValue>{selectedEvent.eventType}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>기간</DetailLabel>
                    <DetailValue>{selectedEvent.startDate} ~ {selectedEvent.endDate}</DetailValue>
                  </DetailRow>
                  {selectedEvent.openDatetime && (
                    <DetailRow>
                      <DetailLabel>오픈 시간</DetailLabel>
                      <DetailValue>
                        {selectedEvent.openDatetime}
                        {(() => {
                          try {
                            const now = new Date().getTime();
                            // 'YYYY-MM-DD HH:mm:ss' 형식을 ISO 형식으로 변환
                            const isoFormat = selectedEvent.openDatetime!.replace(' ', 'T');
                            const openTime = new Date(isoFormat).getTime();
                            if (!isNaN(openTime) && now < openTime) {
                              return (
                                <div style={{ marginTop: '0.5rem' }}>
                                  {/* 여기입니다~ 실시간으로 1초씩 줄어들어서 나오는 디데이 표시기 */}
                                  <CountdownDisplay openDatetime={selectedEvent.openDatetime!} />
                                </div>
                              );
                            }
                          } catch (error) {
                            console.error('오픈 시간 체크 중 오류:', error);
                          }
                          return null;
                        })()}
                      </DetailValue>
                    </DetailRow>
                  )}
                  <DetailRow>
                    <DetailLabel>참가 인원</DetailLabel>
                    <DetailValue>{selectedEvent.currentParticipants}/{selectedEvent.maxParticipants}명</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>상태</DetailLabel>
                    <DetailValue>
                      <StatusBadge status={selectedEvent.status}>
                        {selectedEvent.status}
                      </StatusBadge>
                    </DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>설명</DetailLabel>
                    <DetailContent>{selectedEvent.description}</DetailContent>
                  </DetailRow>
                </ViewSection>
                
                {/* 배번할당 이벤트인 경우 디자인/사이즈 표 버튼은 참여 여부와 관계없이 항상 표시 */}
                {selectedEvent.eventType === '배번할당' && (
                  <ApplicationForm>
                    <FormTitle>디자인 및 사이즈 표</FormTitle>
                    <ImageButtonGroup>
                      <ImageButton
                        onClick={() => {
                          const imageUrl = `${API_BASE_URL}/data/design.png`;
                          setCurrentImageUrl(imageUrl);
                          setCurrentImageTitle('디자인');
                          setIsImageModalOpen(true);
                        }}
                      >
                        🎨 디자인보기
                      </ImageButton>
                      <ImageButton
                        onClick={() => {
                          // 여자 버전 상의
                          const imageUrl = `${API_BASE_URL}/data/${encodeURIComponent('size upper.jpeg')}`;
                          setCurrentImageUrl(imageUrl);
                          setCurrentImageTitle('상의 사이즈 표 (여자)');
                          setIsImageModalOpen(true);
                        }}
                      >
                        📏 상의 사이즈 표 (여자)
                      </ImageButton>
                      <ImageButton
                        onClick={() => {
                          // 공용 버전 상의
                          const imageUrl = `${API_BASE_URL}/data/${encodeURIComponent('size upper m.jpeg')}`;
                          setCurrentImageUrl(imageUrl);
                          setCurrentImageTitle('상의 사이즈 표 (공용)');
                          setIsImageModalOpen(true);
                        }}
                      >
                        📏 상의 사이즈 표 (공용)
                      </ImageButton>
                      <ImageButton
                        onClick={() => {
                          // 여자 버전 하의
                          const imageUrl = `${API_BASE_URL}/data/${encodeURIComponent('size lower.jpeg')}`;
                          setCurrentImageUrl(imageUrl);
                          setCurrentImageTitle('하의 사이즈 표 (여자)');
                          setIsImageModalOpen(true);
                        }}
                      >
                        📏 하의 사이즈 표 (여자)
                      </ImageButton>
                      <ImageButton
                        onClick={() => {
                          // 공용 버전 하의
                          const imageUrl = `${API_BASE_URL}/data/${encodeURIComponent('size lower m.jpeg')}`;
                          setCurrentImageUrl(imageUrl);
                          setCurrentImageTitle('하의 사이즈 표 (공용)');
                          setIsImageModalOpen(true);
                        }}
                      >
                        📏 하의 사이즈 표 (공용)
                      </ImageButton>
                    </ImageButtonGroup>
                  </ApplicationForm>
                )}

                {!selectedEvent.isApplied && selectedEvent.status === '진행중' && (
                  <>
                    {isBeforeOpenTime && (
                      <div style={{
                        padding: '1rem',
                        background: '#fff3cd',
                        borderRadius: '8px',
                        marginBottom: '1rem',
                        border: '1px solid #ffc107'
                      }}>
                        <p style={{ 
                          fontSize: '0.875rem', 
                          color: '#856404', 
                          fontWeight: 600,
                          margin: 0,
                          textAlign: 'center'
                        }}>
                          ⏰ 이벤트 오픈 시간 전입니다
                          <br />
                          <span style={{ fontSize: '0.8125rem', fontWeight: 400 }}>
                            오픈 시간: {selectedEvent.openDatetime}
                          </span>
                        </p>
                      </div>
                    )}
                    {selectedEvent.eventType === '배번변경' && (
                      <ApplicationForm>
                        <FormTitle>
                          {hasUniformNumber ? '배번 변경 신청' : '배번 할당 신청'}
                        </FormTitle>
                        <p style={{ fontSize: '0.875rem', color: '#6c757d', lineHeight: 1.6 }}>
                          {hasUniformNumber ? (
                            <>
                              현재 가지고 있는 배번을 다른 회원이 신청할 수 있도록 허용하는 이벤트입니다.
                              <br />
                              변경 신청 후, 추후 다른 회원이 해당 배번을 선택하여 사용하게 되면
                              <br />
                              <span style={{ color: '#dc3545', fontWeight: 700 }}>
                                기존에 사용하던 배번은 더 이상 사용할 수 없을 수도 있습니다.
                              </span>
                              <br />
                              아래 버튼을 눌러 이벤트에 참가만 해 두시면 됩니다.
                            </>
                          ) : (
                            <>
                              배번이 없는 회원을 위한 배번 할당 이벤트입니다.
                              <br />
                              이벤트 종료 후 선착순으로 배번이 할당됩니다.
                              <br />
                              아래 버튼을 눌러 이벤트에 참가해주세요.
                            </>
                          )}
                        </p>
                      </ApplicationForm>
                    )}
                    {selectedEvent.eventType === '배번할당' && !selectedEvent.isApplied && selectedEvent.status === '진행중' && (
                      <ApplicationForm>
                        <FormTitle>배번 할당 신청</FormTitle>
                        {/* 사용 가능한 배번 목록 표시 */}
                        {loadingAvailableNumbers ? (
                          <p style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '1rem' }}>
                            사용 가능한 배번을 불러오는 중...
                          </p>
                        ) : availableNumbers.length > 0 ? (
                          <div style={{ marginBottom: '1rem' }}>
                            <p style={{ fontSize: '0.875rem', color: '#6c757d', lineHeight: 1.6, marginBottom: '0.75rem' }}>
                              선택 가능한 배번 ({availableNumbers.length}개):
                            </p>
                            <div style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '0.5rem',
                              padding: '0.75rem',
                              background: '#f8f9fa',
                              borderRadius: '8px',
                              maxHeight: '200px',
                              overflowY: 'auto'
                            }}>
                              {availableNumbers.map((num) => (
                                <button
                                  key={num}
                                  type="button"
                                  onClick={() => {
                                    setDesiredNumber(num);
                                    setNumberError('');
                                  }}
                                  style={{
                                    padding: '0.5rem 0.75rem',
                                    background: desiredNumber === num ? '#000000' : '#ffffff',
                                    color: desiredNumber === num ? '#ffffff' : '#000000',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    fontWeight: desiredNumber === num ? 600 : 400,
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (desiredNumber !== num) {
                                      e.currentTarget.style.background = '#e9ecef';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (desiredNumber !== num) {
                                      e.currentTarget.style.background = '#ffffff';
                                    }
                                  }}
                                >
                                  {num}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p style={{ fontSize: '0.875rem', color: '#dc3545', marginBottom: '1rem' }}>
                            사용 가능한 배번이 없습니다.
                          </p>
                        )}
                        <p style={{ fontSize: '0.875rem', color: '#6c757d', lineHeight: 1.6, marginBottom: '1rem' }}>
                          원하는 배번, 닉네임, 사이즈를 입력해주세요. (1-99번)
                          <br />
                          선착순으로 배번이 할당됩니다.
                        </p>
                        <div style={{ marginBottom: '1rem' }}>
                          <Input
                            label="희망 배번"
                            type="number"
                            value={desiredNumber}
                            onChange={(e) => {
                              const value = e.target.value;
                              // 숫자만 입력 가능 (1-99 범위)
                              if (value === '' || /^\d+$/.test(value)) {
                                const num = value === '' ? 0 : parseInt(value);
                                if (value === '' || (num >= 1 && num <= 99)) {
                                  setDesiredNumber(value);
                                  setNumberError('');
                                }
                              }
                            }}
                            placeholder="예: 22 (1-99번)"
                            required
                          />
                          {numberError && (
                            <p style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                              {numberError}
                            </p>
                          )}
                        </div>
                        <div style={{ marginBottom: '1rem' }}>
                          <Input
                            label="닉네임"
                            type="text"
                            value={nickname}
                            onChange={(e) => {
                              setNickname(e.target.value);
                              setNicknameError('');
                            }}
                            placeholder="예: 철수"
                            required
                          />
                          {nicknameError && (
                            <p style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                              {nicknameError}
                            </p>
                          )}
                        </div>
                        <div style={{ marginBottom: '1rem' }}>
                          <Input
                            label="상의 사이즈 (사이즈 표 확인 후 여자 사이즈를 원하시는 분은 w를 꼭 기입해 주세요)"
                            type="text"
                            value={upperSize}
                            onChange={(e) => {
                              setUpperSize(e.target.value);
                              setSizeError('');
                            }}
                            placeholder="예: S, M, L, XL 또는 w M, w L"
                            required
                          />
                        </div>
                        <div style={{ marginBottom: '1rem' }}>
                          <Input
                            label="하의 사이즈 (사이즈 표 확인 후 여자 사이즈를 원하시는 분은 w를 꼭 기입해 주세요)"
                            type="text"
                            value={lowerSize}
                            onChange={(e) => {
                              setLowerSize(e.target.value);
                              setSizeError('');
                            }}
                            placeholder="예: S, M, L, XL 또는 w M, w L"
                            required
                          />
                          {sizeError && (
                            <p style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                              {sizeError}
                            </p>
                          )}
                        </div>
                      </ApplicationForm>
                    )}
                    {selectedEvent.eventType !== '배번변경' && selectedEvent.eventType !== '배번할당' && (
                      <ApplicationForm>
                        <FormTitle>참가 신청</FormTitle>
                        <p style={{ fontSize: '0.875rem', color: '#6c757d', lineHeight: 1.6 }}>
                          아래 버튼을 눌러 이벤트에 참가할 수 있습니다.
                        </p>
                      </ApplicationForm>
                    )}
                    
                    <ModalActions>
                      <Button
                        variant="outline"
                        size="large"
                        fullWidth
                        onClick={() => setIsModalOpen(false)}
                      >
                        닫기
                      </Button>
                      {selectedEvent.eventType === '배번변경' && (
                        <Button
                          variant="primary"
                          size="large"
                          fullWidth
                          onClick={handleApply}
                          disabled={isBeforeOpenTime}
                        >
                          변경 신청
                        </Button>
                      )}
                      {selectedEvent.eventType === '배번할당' && (
                        <Button
                          variant="primary"
                          size="large"
                          fullWidth
                          onClick={handleApply}
                          disabled={isBeforeOpenTime}
                        >
                          배번 신청
                        </Button>
                      )}
                      {selectedEvent.eventType !== '배번변경' && selectedEvent.eventType !== '배번할당' && (
                        <Button
                          variant="primary"
                          size="large"
                          fullWidth
                          onClick={handleApply}
                          disabled={isBeforeOpenTime}
                        >
                          참가 신청
                        </Button>
                      )}
                    </ModalActions>
                  </>
                )}
                
                {selectedEvent.isApplied && selectedEvent.status === '진행중' && (
                  <>
                    {/* 배번할당 이벤트인 경우 입력 폼 표시 */}
                    {selectedEvent.eventType === '배번할당' && (
                      <ApplicationForm>
                        <FormTitle>배번 할당 정보 수정</FormTitle>
                        <p style={{ fontSize: '0.875rem', color: '#6c757d', lineHeight: 1.6, marginBottom: '1rem' }}>
                          닉네임과 사이즈만 수정할 수 있습니다. 배번은 수정할 수 없습니다.
                        </p>
                        <div style={{ marginBottom: '1rem' }}>
                          <Input
                            label="희망 배번 (수정 불가)"
                            type="number"
                            value={desiredNumber}
                            onChange={() => {}}
                            disabled
                            placeholder="예: 22 (1-99번)"
                          />
                          <p style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.25rem' }}>
                            배번은 수정할 수 없습니다.
                          </p>
                        </div>
                        <div style={{ marginBottom: '1rem' }}>
                          <Input
                            label="닉네임"
                            type="text"
                            value={nickname}
                            onChange={(e) => {
                              setNickname(e.target.value);
                              setNicknameError('');
                            }}
                            placeholder="예: 철수"
                            required
                          />
                          {nicknameError && (
                            <p style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                              {nicknameError}
                            </p>
                          )}
                        </div>
                        <div style={{ marginBottom: '1rem' }}>
                          <Input
                            label="상의 사이즈 (사이즈 표 확인 후 여자 사이즈를 원하시는 분은 w를 꼭 기입해 주세요)"
                            type="text"
                            value={upperSize}
                            onChange={(e) => {
                              setUpperSize(e.target.value);
                              setSizeError('');
                            }}
                            placeholder="예: S, M, L, XL 또는 w M, w L"
                            required
                          />
                        </div>
                        <div style={{ marginBottom: '1rem' }}>
                          <Input
                            label="하의 사이즈 (사이즈 표 확인 후 여자 사이즈를 원하시는 분은 w를 꼭 기입해 주세요)"
                            type="text"
                            value={lowerSize}
                            onChange={(e) => {
                              setLowerSize(e.target.value);
                              setSizeError('');
                            }}
                            placeholder="예: S, M, L, XL 또는 w M, w L"
                            required
                          />
                          {sizeError && (
                            <p style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                              {sizeError}
                            </p>
                          )}
                        </div>
                      </ApplicationForm>
                    )}
                    <ModalActions>
                      <Button
                        variant="outline"
                        size="large"
                        fullWidth
                        onClick={() => setIsModalOpen(false)}
                      >
                        닫기
                      </Button>
                      {selectedEvent.eventType === '배번할당' && (
                        <Button
                          variant="primary"
                          size="large"
                          fullWidth
                          onClick={handleApply}
                          disabled={isBeforeOpenTime}
                        >
                          정보 수정
                        </Button>
                      )}
                      <Button
                        variant="danger"
                        size="large"
                        fullWidth
                        onClick={handleCancelApply}
                      >
                        참가 취소
                      </Button>
                    </ModalActions>
                  </>
                )}
                
                {selectedEvent.status !== '진행중' && !selectedEvent.isApplied && (
                  <ModalActions>
                    <Button
                      variant="outline"
                      size="large"
                      fullWidth
                      onClick={() => setIsModalOpen(false)}
                    >
                      닫기
                    </Button>
                  </ModalActions>
                )}
              </>
            )}
          </ModalContent>
        </ModalOverlay>
        
        {/* 이미지 모달 */}
        <ImageModalOverlay 
          isOpen={isImageModalOpen}
          onClick={() => setIsImageModalOpen(false)}
        >
          <ImageModalContent onClick={(e) => e.stopPropagation()}>
            <ImageModalHeader>
              <ImageModalTitle>{currentImageTitle}</ImageModalTitle>
              <ImageModalCloseButton onClick={() => setIsImageModalOpen(false)}>
                ×
              </ImageModalCloseButton>
            </ImageModalHeader>
            <ImageContainer>
              <ModalImage 
                src={currentImageUrl} 
                alt={currentImageTitle}
                onError={(e) => {
                  console.error('이미지 로드 실패:', currentImageUrl);
                  e.currentTarget.src = ''; // 에러 이미지 제거
                }}
              />
            </ImageContainer>
          </ImageModalContent>
        </ImageModalOverlay>
      </EventsContainer>
    </DashboardLayout>
  );
};

export default MemberEvents;

